import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { EMAIL_REGEX, PHONE_REGEX, normalizeInstagram } from '../utils/validation';

// requireInstagram: ally bank transfers use the Instagram user as the
// transfer reference (concepto), so it's shown and required for any delivery.
const CustomerInfoForm = ({ onCustomerInfoChange, initialData = {}, prefillData = null, deliveryMethod = 'delivery', hideTitle = false, requireInstagram = false }) => {
  const { t } = useTranslation('checkout');
  const [formData, setFormData] = useState({
    name: initialData.name || '',
    email: initialData.email || '',
    phone: initialData.phone || '',
    instagram: initialData.instagram || '',
    address: initialData.address || '',
    city: initialData.city || '',
    state: initialData.state || '',
    zipCode: initialData.zipCode || initialData.zip_code || '',
    country: initialData.country || 'México'
  });

  const [errors, setErrors] = useState({});

  // When parent provides prefillData (e.g. from "Cargar mis datos"), merge into form
  useEffect(() => {
    if (prefillData && typeof prefillData === 'object' && prefillData.email) {
      setFormData(prev => ({
        ...prev,
        name: prefillData.name ?? prev.name,
        email: prefillData.email ?? prev.email,
        phone: prefillData.phone ?? prev.phone,
        instagram: prefillData.instagram ?? prev.instagram,
        address: prefillData.address ?? prev.address,
        city: prefillData.city ?? prev.city,
        state: prefillData.state ?? prev.state,
        zipCode: prefillData.zipCode ?? prefillData.zip_code ?? prev.zipCode,
        country: prefillData.country ?? prev.country
      }));
    }
  }, [prefillData]);

  const validate = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = t('customerInfo.errors.nameRequired');
    }

    if (!formData.email.trim()) {
      newErrors.email = t('customerInfo.errors.emailRequired');
    } else if (!EMAIL_REGEX.test(formData.email)) {
      newErrors.email = t('customerInfo.errors.emailInvalid');
    }

    if (!formData.phone.trim()) {
      newErrors.phone = t('customerInfo.errors.phoneRequired');
    } else if (!PHONE_REGEX.test(formData.phone)) {
      newErrors.phone = t('customerInfo.errors.phoneInvalid');
    }

    // Instagram is optional and only asked for in-store pickup — unless required
    if (requireInstagram && !formData.instagram.trim()) {
      newErrors.instagram = t('customerInfo.errors.instagramRequired');
    } else if ((deliveryMethod === 'personal' || requireInstagram) && normalizeInstagram(formData.instagram) === undefined) {
      newErrors.instagram = t('customerInfo.errors.instagramInvalid');
    }

    // Only validate address fields if delivery method is 'delivery'
    if (deliveryMethod === 'delivery') {
      if (!formData.address.trim()) {
        newErrors.address = t('customerInfo.errors.addressRequired');
      }

      if (!formData.city.trim()) {
        newErrors.city = t('customerInfo.errors.cityRequired');
      }

      if (!formData.state.trim()) {
        newErrors.state = t('customerInfo.errors.stateRequired');
      }

      if (!formData.zipCode.trim()) {
        newErrors.zipCode = t('customerInfo.errors.zipCodeRequired');
      } else if (!/^\d{5}$/.test(formData.zipCode.trim())) {
        newErrors.zipCode = t('customerInfo.errors.zipCodeInvalid');
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      return updated;
    });
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Validate on change and notify parent
  useEffect(() => {
    if (onCustomerInfoChange) {
      const isValid = validate();
      onCustomerInfoChange(formData, isValid);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, deliveryMethod, requireInstagram]);

  return (
    <div className="customer-info-form">
      {!hideTitle && (
        <h3 style={{ marginBottom: '1.5rem', fontSize: '1.25rem', color: '#333' }}>
          {t('customerInfo.heading')}
        </h3>
      )}
      <div className="form-group" style={{ marginBottom: '1rem' }}>
        <label className="form-label">
          {t('customerInfo.fields.name.label')} <span style={{ color: '#dc3545' }}>*</span>
        </label>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          className="form-input"
          placeholder={t('customerInfo.fields.name.placeholder')}
          required
        />
        {errors.name && (
          <span style={{ color: '#dc3545', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
            {errors.name}
          </span>
        )}
      </div>

      <div className="form-group" style={{ marginBottom: '1rem' }}>
        <label className="form-label">
          {t('customerInfo.fields.email.label')} <span style={{ color: '#dc3545' }}>*</span>
        </label>
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          className="form-input"
          placeholder={t('customerInfo.fields.email.placeholder')}
          required
        />
        {errors.email && (
          <span style={{ color: '#dc3545', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
            {errors.email}
          </span>
        )}
      </div>

      <div className="form-group" style={{ marginBottom: '1rem' }}>
        <label className="form-label">
          {t('customerInfo.fields.phone.label')} <span style={{ color: '#dc3545' }}>*</span>
        </label>
        <input
          type="tel"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          className="form-input"
          placeholder={t('customerInfo.fields.phone.placeholder')}
          required
        />
        {errors.phone && (
          <span style={{ color: '#dc3545', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
            {errors.phone}
          </span>
        )}
      </div>

      {/* Store staff ask for the Instagram user at pickup */}
      {(deliveryMethod === 'personal' || requireInstagram) && (
        <div className="form-group" style={{ marginBottom: '1rem' }}>
          <label className="form-label">
            {t('customerInfo.fields.instagram.label')}{requireInstagram && ' *'}
          </label>
          <input
            type="text"
            name="instagram"
            value={formData.instagram}
            onChange={handleChange}
            className="form-input"
            placeholder={t('customerInfo.fields.instagram.placeholder')}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
          />
          {errors.instagram ? (
            <span style={{ color: '#dc3545', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
              {errors.instagram}
            </span>
          ) : (
            <span style={{ color: '#888', fontSize: '0.8rem', marginTop: '0.25rem', display: 'block' }}>
              {requireInstagram ? t('customerInfo.fields.instagram.hintTransfer') : t('customerInfo.fields.instagram.hint')}
            </span>
          )}
        </div>
      )}

      {/* Only show address fields if delivery method is 'delivery' */}
      {deliveryMethod === 'delivery' && (
        <>
          <h4 style={{ marginTop: '1.5rem', marginBottom: '1rem', fontSize: '1.1rem', color: '#333' }}>
            {t('customerInfo.headingShipping')}
          </h4>

          <div className="form-group" style={{ marginBottom: '1rem' }}>
        <label className="form-label">
          {t('customerInfo.fields.address.label')} <span style={{ color: '#dc3545' }}>*</span>
        </label>
        <input
          type="text"
          name="address"
          value={formData.address}
          onChange={handleChange}
          className="form-input"
          placeholder={t('customerInfo.fields.address.placeholder')}
          required
        />
        {errors.address && (
          <span style={{ color: '#dc3545', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
            {errors.address}
          </span>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="form-label">
            {t('customerInfo.fields.city.label')} <span style={{ color: '#dc3545' }}>*</span>
          </label>
          <input
            type="text"
            name="city"
            value={formData.city}
            onChange={handleChange}
            className="form-input"
            placeholder={t('customerInfo.fields.city.placeholder')}
            required
          />
          {errors.city && (
            <span style={{ color: '#dc3545', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
              {errors.city}
            </span>
          )}
        </div>

        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="form-label">
            {t('customerInfo.fields.state.label')} <span style={{ color: '#dc3545' }}>*</span>
          </label>
          <input
            type="text"
            name="state"
            value={formData.state}
            onChange={handleChange}
            className="form-input"
            placeholder={t('customerInfo.fields.state.placeholder')}
            required
          />
          {errors.state && (
            <span style={{ color: '#dc3545', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
              {errors.state}
            </span>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="form-label">
            {t('customerInfo.fields.zipCode.label')} <span style={{ color: '#dc3545' }}>*</span>
          </label>
          <input
            type="text"
            name="zipCode"
            value={formData.zipCode}
            onChange={handleChange}
            className="form-input"
            placeholder={t('customerInfo.fields.zipCode.placeholder')}
            maxLength="5"
            required
          />
          {errors.zipCode && (
            <span style={{ color: '#dc3545', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
              {errors.zipCode}
            </span>
          )}
        </div>

        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="form-label">
            {t('customerInfo.fields.country.label')} <span style={{ color: '#dc3545' }}>*</span>
          </label>
          <input
            type="text"
            name="country"
            value={formData.country}
            onChange={handleChange}
            className="form-input"
            placeholder={t('customerInfo.fields.country.placeholder')}
            required
          />
        </div>
      </div>
        </>
      )}
    </div>
  );
};

export default CustomerInfoForm;
